import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarCheck, GraduationCap, Crown, Users, LogOut, ShieldAlert } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const TABS = [
  { id: "bookings", label: "Appointments", ep: "/admin/bookings" },
  { id: "enrollments", label: "Academy", ep: "/admin/enrollments" },
  { id: "bridal", label: "Bridal", ep: "/admin/bridal" },
  { id: "leads", label: "Leads", ep: "/admin/leads" },
];

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("bookings");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/admin/login"); return; }
    if (!user.is_admin) return;
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user?.is_admin) return;
    const ep = TABS.find((t) => t.id === tab).ep;
    api.get(ep).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [tab, user]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/${tab}/${id}`, { status });
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success("Updated");
    } catch { toast.error("Could not update"); }
  };

  if (loading) return <div className="min-h-screen bg-midnight flex items-center justify-center text-gold font-label text-sm">Loading…</div>;

  if (user && !user.is_admin) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center px-5 text-center">
        <div><ShieldAlert className="text-gold mx-auto" size={40} /><h1 className="text-2xl mt-4">Access Restricted</h1><p className="text-white/60 mt-2 text-sm">{user.email} is not an admin account.</p><button onClick={() => { logout(); navigate("/"); }} className="btn-ghost mt-6" data-testid="admin-logout-denied">Sign Out</button></div>
      </div>
    );
  }

  const statCards = stats ? [
    { icon: CalendarCheck, label: "Appointments", value: stats.bookings },
    { icon: GraduationCap, label: "Academy Enquiries", value: stats.enrollments },
    { icon: Crown, label: "Bridal Enquiries", value: stats.bridal },
    { icon: Users, label: "Total Leads", value: stats.leads },
  ] : [];
  const chartData = statCards.map((s) => ({ name: s.label.split(" ")[0], count: s.value }));

  return (
    <div className="min-h-screen bg-[#020a22] text-white">
      <div className="border-b border-gold/20 bg-[#0A1435]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3"><span className="font-serif text-lg gold-text">Spunky Scissors</span><span className="text-white/40 text-xs font-label">Admin</span></div>
          <div className="flex items-center gap-4">
            <span className="text-white/60 text-sm hidden sm:block">{user?.email}</span>
            <button onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-2 text-white/70 hover:text-gold text-sm" data-testid="admin-logout"><LogOut size={16} />Logout</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((s) => (
            <div key={s.label} className="bg-[#0A1435] border border-gold/15 rounded-lg p-5" data-testid={`stat-${s.label.split(" ")[0].toLowerCase()}`}>
              <s.icon className="text-gold" size={22} /><div className="text-3xl font-serif gold-text mt-3">{s.value ?? "–"}</div><div className="text-white/50 text-xs mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="bg-[#0A1435] border border-gold/15 rounded-lg p-5 mt-6 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="name" stroke="#ffffff60" fontSize={12} /><YAxis stroke="#ffffff60" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#020a22", border: "1px solid #D4AF37", borderRadius: 6 }} />
              <Bar dataKey="count" fill="#D4AF37" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="flex flex-wrap gap-2 mt-8 mb-4">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-2 text-sm rounded border transition-colors ${tab === t.id ? "bg-gold text-midnight border-gold" : "border-gold/30 text-white/70 hover:border-gold"}`}>{t.label}</button>
          ))}
        </div>

        <div className="bg-[#0A1435] border border-gold/15 rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-table">
            <thead><tr className="border-b border-gold/15 text-gold text-left font-label text-xs">
              <th className="p-4">Name</th><th className="p-4">Contact</th><th className="p-4">Detail</th><th className="p-4">Date</th><th className="p-4">Status</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-white/40">No records yet.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-4">{r.name}</td>
                  <td className="p-4 text-white/60">{r.phone}<br /><span className="text-xs">{r.email}</span></td>
                  <td className="p-4 text-white/70">{r.service || r.course || r.package || r.source || "-"}{r.date ? ` · ${r.date} ${r.time || ""}` : ""}{r.event_date ? ` · ${r.event_date}` : ""}</td>
                  <td className="p-4 text-white/40 text-xs">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="p-4">
                    {(tab === "bookings" || tab === "enrollments") ? (
                      <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} data-testid={`status-${r.id}`}
                        className="bg-[#020a22] border border-gold/30 rounded px-2 py-1 text-xs">
                        {(tab === "bookings" ? ["pending", "confirmed", "completed", "cancelled"] : ["new", "contacted", "enrolled", "closed"]).map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : <span className="text-white/50 text-xs">{r.status || "new"}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
